import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ContentCard from "@/components/base/ContentCard";
import NotFoundBlock from "@/components/base/NotFoundBlock";
import ShareModal from "@/components/base/ShareModal";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import usePageMeta from "@/hooks/usePageMeta";
import { newsItems } from "@/mocks/content";
import type { RichBlock } from "@/mocks/content";
import { formatDateTime, formatViews } from "@/lib/format";

function RichContent({ blocks }: { blocks: RichBlock[] }) {
  return (
    <div className="mt-6 space-y-5">
      {blocks.map((block, index) => {
        if (block.type === "heading") {
          return (
            <h2
              key={index}
              className="mt-8 flex items-center gap-2 border-l-2 border-primary-500 pl-3 font-heading text-lg font-bold text-foreground-950 md:text-xl"
            >
              {block.text}
            </h2>
          );
        }
        if (block.type === "quote") {
          return (
            <blockquote
              key={index}
              className="rounded-md border-l-2 border-primary-400 bg-background-100 px-4 py-3 text-sm italic leading-relaxed text-foreground-700"
            >
              {block.text}
            </blockquote>
          );
        }
        if (block.type === "image") {
          return (
            <figure key={index} className="my-6">
              <div className="aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
                <img
                  src={block.src}
                  alt={block.caption}
                  title={`${block.caption} - 红桃网资讯配图`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <figcaption className="mt-2 text-center text-xs text-foreground-400">{block.caption}</figcaption>
            </figure>
          );
        }
        return (
          <p key={index} className="text-[15px] leading-[1.9] text-foreground-800">
            {block.text}
          </p>
        );
      })}
    </div>
  );
}

export default function NewsDetailPage() {
  const { id } = useParams();
  const article = newsItems.find((item) => item.id === id);
  const [shareOpen, setShareOpen] = useState(false);

  usePageMeta(
    article ? `${article.title} - ${article.categoryLabel} - 红桃网` : "内容不存在 - 红桃网",
    article
      ? `${article.summary.slice(0, 70)} 红桃网娱乐资讯，为你梳理事件经过与行业背景。`
      : "你访问的资讯不存在或已下线，请返回娱乐资讯频道继续浏览。",
    article ? `${article.tags.join(",")},红桃网,娱乐资讯` : "红桃网,娱乐资讯",
  );

  const related = useMemo(() => {
    if (!article) return [];
    const sameType = newsItems.filter((item) => item.id !== article.id && item.newsType === article.newsType);
    const others = newsItems.filter((item) => item.id !== article.id && item.newsType !== article.newsType);
    return [...sameType, ...others].slice(0, 3);
  }, [article]);

  const hotNews = useMemo(
    () => [...newsItems].sort((a, b) => b.views - a.views).slice(0, 8),
    [],
  );

  if (!article) {
    return <NotFoundBlock title="内容不存在" backTo="/news" backLabel="返回娱乐资讯" />;
  }

  return (
    <>
      <div className="mx-auto w-full max-w-portal px-4 py-5 md:px-6 md:py-7">
        <Breadcrumbs
          items={[
            { label: "首页", to: "/" },
            { label: "娱乐资讯", to: "/news" },
            { label: article.categoryLabel },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-9">
          <article className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to="/news"
                className="rounded-sm bg-primary-50 px-2 py-1 text-[11px] text-primary-700 transition-colors hover:bg-primary-100"
              >
                {article.categoryLabel}
              </Link>
              <span className="text-xs text-foreground-400">{formatDateTime(article.publishedAt)}</span>
              <span className="text-foreground-200">·</span>
              <span className="flex items-center gap-1 text-xs text-foreground-400">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 次阅读
              </span>
            </div>

            <h1 className="mt-3 font-heading text-[26px] font-black leading-snug text-foreground-950 md:text-[34px]">
              {article.title}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-500 md:text-[15px]">{article.summary}</p>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-y border-background-200 py-3">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-50 font-heading text-sm font-bold text-primary-600">
                  {article.author.slice(0, 1)}
                </span>
                <div>
                  <p className="text-[13px] font-medium text-foreground-900">{article.author}</p>
                  <p className="text-[11px] text-foreground-400">{article.source}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-share-forward-line" />
                分享
              </button>
            </div>

            <div className="mt-5 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 红桃网娱乐资讯`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <RichContent blocks={article.content} />

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-5">
              <Link
                to="/news"
                className="flex items-center gap-1 rounded-sm bg-primary-50 px-2.5 py-1 text-xs text-primary-700 transition-colors hover:bg-primary-100"
              >
                <i className="ri-folder-3-line" />
                所属分类：{article.categoryLabel}
              </Link>
              <span className="text-xs text-foreground-400">相关标签</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-5 flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1 text-primary-500" />
                本文为虚构演示内容，仅用于展示页面结构与阅读体验，不构成任何真实资讯。
              </p>
              <Link
                to="/news"
                className="flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回娱乐资讯
              </Link>
            </div>

            <section className="mt-9">
              <div className="mb-4 flex items-end justify-between gap-4 border-b border-background-200 pb-3">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <span className="h-4 w-1 rounded-full bg-primary-500" />
                  相关推荐
                </h2>
                <Link to="/news" className="flex items-center gap-0.5 text-xs text-foreground-500 hover:text-primary-600">
                  更多资讯
                  <i className="ri-arrow-right-s-line" />
                </Link>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {related.map((item) => (
                  <ContentCard key={item.id} item={item} showSummaryAlways />
                ))}
              </div>
            </section>
          </article>

          <aside className="space-y-4 self-start lg:sticky lg:top-[116px]">
            <div className="rounded-lg border border-background-200 bg-background-50">
              <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
                <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-foreground-950">
                  <i className="ri-fire-fill text-primary-500" />
                  热门资讯
                </h2>
                <span className="font-label text-[10px] tracking-widest text-foreground-400">热榜</span>
              </div>
              <ol className="divide-y divide-background-100 px-4">
                {hotNews.map((item, index) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="group flex items-start gap-3 py-3">
                      <span
                        className={[
                          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-sm font-label text-[11px] font-bold",
                          index < 3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500",
                        ].join(" ")}
                      >
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] leading-snug text-foreground-800 transition-colors group-hover:text-primary-600 line-clamp-2">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-[11px] text-foreground-400">
                          {formatViews(item.views)} 次阅读
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>

            <div className="rounded-lg bg-primary-950 p-4 text-background-100">
              <p className="font-label text-[11px] tracking-[0.22em] text-accent-400">关注我们</p>
              <p className="mt-2 text-sm font-semibold text-background-50">想看更多现场报道？</p>
              <p className="mt-1.5 text-xs leading-relaxed text-primary-100/80">
                红桃网每天更新娱乐资讯。你也可以去互动专区，和其他读者一起聊聊刚看完的作品。
              </p>
              <Link
                to="/community"
                className="mt-3 inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-background-50 px-4 py-2 text-xs text-foreground-950 transition-colors hover:bg-accent-400"
              >
                去互动专区
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </aside>
        </div>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={article.title} />
    </>
  );
}